import {
  CadPropertyType,
  ICadProperty,
  ICadPropertyCategory,
  ICadSelectedEntity,
} from 'Data/interfaces/CadViewer/ICadProperty';

const NUMERIC_TYPES = new Set<CadPropertyType>(['int', 'float']);
const VARIES_LABEL = '*Varia*';

type LabelOccurrence = {
  prop: ICadProperty;
  entityIndex: number;
};

export function aggregateCategories(
  entities: ICadSelectedEntity[],
  formatLength?: (value: number) => string
): ICadPropertyCategory[] {
  if (entities.length === 0) return [];
  if (entities.length === 1) return entities[0].categories;

  const categoryOrder: string[] = [];
  const categoryMap = new Map<string, ICadPropertyCategory[]>();

  for (const entity of entities) {
    for (const category of entity.categories) {
      if (!categoryMap.has(category.categoryName)) {
        categoryMap.set(category.categoryName, []);
        categoryOrder.push(category.categoryName);
      }
      categoryMap.get(category.categoryName)!.push(category);
    }
  }

  const result: ICadPropertyCategory[] = [];

  for (const categoryName of categoryOrder) {
    const occurrences = categoryMap.get(categoryName)!;
    // Category must exist in ALL entities to appear in the aggregated view.
    if (occurrences.length !== entities.length) continue;

    const aggregated = aggregateProperties(occurrences, entities.length, formatLength);
    if (aggregated.length === 0) continue;

    result.push({ categoryName, properties: aggregated });
  }

  return result;
}

function aggregateProperties(
  categoryOccurrences: ICadPropertyCategory[],
  entitiesCount: number,
  formatLength?: (value: number) => string
): ICadProperty[] {
  const labelOrder: string[] = [];
  const labelMap = new Map<string, LabelOccurrence[]>();

  categoryOccurrences.forEach((category, entityIndex) => {
    for (const prop of category.properties) {
      if (!labelMap.has(prop.label)) {
        labelMap.set(prop.label, []);
        labelOrder.push(prop.label);
      }
      labelMap.get(prop.label)!.push({ prop, entityIndex });
    }
  });

  const result: ICadProperty[] = [];

  for (const label of labelOrder) {
    const occurrences = labelMap.get(label)!;
    // Property must exist in ALL entities for this category to appear.
    if (occurrences.length !== entitiesCount) continue;

    // Skip array properties — aggregating arrays makes no sense.
    if (occurrences.some(o => o.prop.propertyType === 'array')) continue;

    const allNumeric = occurrences.every(
      o => o.prop.propertyType && NUMERIC_TYPES.has(o.prop.propertyType)
    );

    if (allNumeric) {
      result.push(buildSumProperty(label, occurrences, formatLength));
      continue;
    }

    result.push(buildIntersectionProperty(label, occurrences));
  }

  return result;
}

function buildSumProperty(
  label: string,
  occurrences: LabelOccurrence[],
  formatLength?: (value: number) => string
): ICadProperty {
  let sum = 0;
  const values: string[] = [];

  for (const { prop } of occurrences) {
    const n = typeof prop.rawValue === 'number' ? prop.rawValue : Number(prop.rawValue);
    if (Number.isFinite(n)) sum += n;
    values.push(prop.value);
  }

  return {
    label,
    value: formatLength ? formatLength(sum) : String(sum),
    rawValue: sum,
    propertyType: occurrences[0].prop.propertyType,
    aggregation: 'sum',
    count: occurrences.length,
    values,
  };
}

function buildIntersectionProperty(
  label: string,
  occurrences: LabelOccurrence[]
): ICadProperty {
  const firstValue = occurrences[0].prop.value;
  const allEqual = occurrences.every(o => o.prop.value === firstValue);

  if (allEqual) {
    return {
      label,
      value: firstValue,
      rawValue: occurrences[0].prop.rawValue,
      propertyType: occurrences[0].prop.propertyType,
      aggregation: 'single',
      count: occurrences.length,
    };
  }

  return {
    label,
    value: VARIES_LABEL,
    rawValue: null,
    propertyType: occurrences[0].prop.propertyType,
    aggregation: 'mixed',
    count: occurrences.length,
    values: occurrences.map(o => o.prop.value),
  };
}

export const CAD_PROPERTY_VARIES_LABEL = VARIES_LABEL;
